import type { z } from 'zod';

import { config } from '@/shared/config';

import { type AppError, createAppError, isAppError } from './AppError';
import { apiErrorSchema, envelopeSchema } from './envelope';

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Staff and patients authenticate on **separate guards**, against separate
 * token stores server-side (CLAUDE.md §3.3). One browser can hold both — the
 * doctor booking their own appointment, or anyone developing this — so the
 * client keeps them apart too. A single slot would mean whichever store
 * rehydrated last decided who every request was from.
 */
export type AuthScope = 'staff' | 'account';

interface RequestOptions<TSchema extends z.ZodTypeAny> {
  readonly method?: Method;
  /**
   * JSON by default. A `FormData` is sent as-is with **no** `Content-Type`:
   * the browser has to set that header itself so it can append the multipart
   * boundary, and setting it by hand produces a body the server cannot parse.
   * That is the only reason this accepts two kinds of body.
   */
  readonly body?: unknown;
  readonly schema: TSchema;
  readonly signal?: AbortSignal;
  /**
   * Defaults to `staff` because the panel was built first and every existing
   * call site means it. Patient endpoints pass `account` explicitly; a public
   * endpoint may send either, and the server ignores what it does not need.
   */
  readonly auth?: AuthScope;
  /**
   * Give up after this long and fail as a connection error.
   *
   * Off by default — most screens are better served by the browser's own
   * patience. It exists for the desk's scanner: on a weak connection a request
   * could hang for minutes, and a spinner that never resolves is a scan the
   * operator believes went through (the lost-admission incident, 2026-10-02).
   * A timeout turns it into a «ارتباط برقرار نشد» the caller can retry.
   */
  readonly timeoutMs?: number;
  /**
   * The language to ask for, when the answer is cached under it. The default
   * follows the UI, but only once the locale provider's effect has run — after
   * its children's first queries. A response keyed by language must say which
   * one it asked for itself.
   */
  readonly locale?: string;
}

const authTokens: Record<AuthScope, string | null> = { staff: null, account: null };
let currentLocale: string = config.defaultLocale;

export function setAuthToken(token: string | null, scope: AuthScope = 'staff'): void {
  authTokens[scope] = token;
}

export function setRequestLocale(locale: string): void {
  currentLocale = locale;
}

/**
 * `crypto.randomUUID` only exists in a **secure context**. The clinic is served
 * over plain HTTP on an IP address today, where it is `undefined` — calling it
 * threw before `fetch` was ever reached, so no request left the browser and
 * every screen looked frozen. A correlation id is a log-tracing token, not a
 * secret, so a non-cryptographic fallback is the right trade: it must never be
 * the reason a request cannot be made.
 */
function correlationId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(16));

    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  }

  return `${Date.now().toString(16)}${Math.random().toString(16).slice(2, 14)}`;
}

/**
 * A `fetch` that throws never reached the server, so «ارتباط با سرور برقرار
 * نشد» was read at the desk as *the server is down* — the clinic asked whether
 * their server was too weak. The browser does know the likelier answer: with
 * `navigator.onLine === false` the request never left the device at all. Name
 * that case separately, and say «اینترنت را بررسی کنید» in both, so the
 * message points at what the reader can actually check.
 */
function connectionErrorCode(): 'common.offline' | 'common.network_error' {
  return typeof navigator !== 'undefined' && !navigator.onLine
    ? 'common.offline'
    : 'common.network_error';
}

/**
 * The single HTTP entry point (CLAUDE.md §7.4). Every response is validated
 * with a zod schema at the boundary, so the UI never touches a raw API shape,
 * and every failure is normalized to AppError.
 */
export async function request<TSchema extends z.ZodTypeAny>(
  path: string,
  options: RequestOptions<TSchema>,
): Promise<z.infer<TSchema>> {
  try {
    return await perform(path, options);
  } catch (cause) {
    // "Normalized to AppError" has to mean *every* failure, including a bug in
    // this module. An error without a `code` renders as an empty alert — the
    // UI then looks like it ignored the click, which is worse than any message.
    throw isAppError(cause) ? cause : createAppError({ status: 0, cause });
  }
}

async function perform<TSchema extends z.ZodTypeAny>(
  path: string,
  options: RequestOptions<TSchema>,
): Promise<z.infer<TSchema>> {
  if (options.timeoutMs === undefined) {
    return send(path, options, options.signal);
  }

  const controller = new AbortController();
  const timer = setTimeout((): void => {
    controller.abort();
  }, options.timeoutMs);
  const forward = (): void => {
    controller.abort();
  };

  // the caller's own cancellation still wins — a timeout only adds a reason
  options.signal?.addEventListener('abort', forward, { once: true });

  if (options.signal?.aborted === true) {
    controller.abort();
  }

  try {
    return await send(path, options, controller.signal);
  } catch (cause) {
    // A body read cut short by the abort surfaces as a parse failure, not a
    // fetch rejection; either way the request did not complete in time.
    if (controller.signal.aborted) {
      throw createAppError({ code: connectionErrorCode(), status: 0, cause });
    }

    throw cause;
  } finally {
    clearTimeout(timer);
    options.signal?.removeEventListener('abort', forward);
  }
}

async function send<TSchema extends z.ZodTypeAny>(
  path: string,
  options: RequestOptions<TSchema>,
  signal: AbortSignal | undefined,
): Promise<z.infer<TSchema>> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Accept-Language': options.locale ?? currentLocale,
    'X-Correlation-Id': correlationId(),
  };

  const isMultipart = options.body instanceof FormData;

  if (options.body !== undefined && !isMultipart) {
    headers['Content-Type'] = 'application/json';
  }

  const authToken = authTokens[options.auth ?? 'staff'];

  if (authToken !== null) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  let response: Response;

  try {
    response = await fetch(`${config.apiBaseUrl}${path}`, {
      method: options.method ?? 'GET',
      headers,
      credentials: 'same-origin',
      ...(options.body === undefined
        ? {}
        : { body: isMultipart ? options.body : JSON.stringify(options.body) }),
      ...(signal ? { signal } : {}),
    });
  } catch {
    throw createAppError({ code: connectionErrorCode(), status: 0 });
  }

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    throw toAppError(payload, response.status);
  }

  const parsed = envelopeSchema(options.schema).safeParse(payload);

  if (!parsed.success) {
    // a contract drift is a bug, not a user-facing failure
    throw createAppError({ code: 'common.invalid_response', status: response.status });
  }

  return parsed.data.data;
}

function toAppError(payload: unknown, status: number): AppError {
  const envelope =
    typeof payload === 'object' && payload !== null && 'errors' in payload ? payload.errors : null;

  const first = Array.isArray(envelope) ? (envelope[0] as unknown) : null;
  const parsed = apiErrorSchema.safeParse(first);

  if (!parsed.success) {
    return createAppError({ status });
  }

  const { code, message, fields, context, correlationId } = parsed.data;

  return createAppError({
    status,
    code,
    message,
    ...(fields === undefined ? {} : { fields }),
    ...(context === undefined ? {} : { context }),
    ...(correlationId === undefined ? {} : { correlationId }),
  });
}

/**
 * Fetch a file the API will only hand over after a permission check — an
 * uploaded medical document (CLAUDE.md §3.7).
 *
 * Separate from `request` on purpose: there is no envelope and no schema to
 * validate, because the body is bytes. Everything else is shared — the same
 * base address, the same auth scopes, the same normalization of a failure into
 * an `AppError` so a 404 renders as a sentence rather than a broken tab.
 *
 * A plain `<a href>` would not do: these routes need an `Authorization` header,
 * and a browser navigation cannot carry one. So the bytes come back here and
 * the caller turns them into a short-lived object URL.
 */
export async function download(
  path: string,
  options: { readonly auth?: AuthScope; readonly signal?: AbortSignal } = {},
): Promise<Blob> {
  return (await fetchRaw(path, options)).blob();
}

/**
 * The same door as `download`, for a body that is text rather than bytes — the
 * website preview, which is a whole HTML document and not an envelope. Read as
 * text here rather than as a Blob, because a Blob's own `text()` is not
 * available everywhere this code runs.
 */
export async function requestText(
  path: string,
  options: { readonly auth?: AuthScope; readonly signal?: AbortSignal } = {},
): Promise<string> {
  return (await fetchRaw(path, options)).text();
}

async function fetchRaw(
  path: string,
  options: { readonly auth?: AuthScope; readonly signal?: AbortSignal },
): Promise<Response> {
  const headers: Record<string, string> = {
    'Accept-Language': currentLocale,
    'X-Correlation-Id': correlationId(),
  };

  const authToken = authTokens[options.auth ?? 'staff'];

  if (authToken !== null) {
    headers.Authorization = `Bearer ${authToken}`;
  }

  let response: Response;

  try {
    response = await fetch(`${config.apiBaseUrl}${path}`, {
      method: 'GET',
      headers,
      credentials: 'same-origin',
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch {
    throw createAppError({ code: connectionErrorCode(), status: 0 });
  }

  if (!response.ok) {
    // The refusal still arrives as JSON, so the shared mapper applies and the
    // patient sees «یافت نشد» rather than a downloaded error page.
    const payload: unknown = await response.json().catch(() => null);

    throw toAppError(payload, response.status);
  }

  return response;
}
